import { AppActionTypes } from "@app/store/actions/app.actions";
import { CreateTogglePropertySuccess, DeleteTogglePropertySuccess } from "@app/store/actions/toggle-property.actions";
import { ContributorBadge } from "@core/enums/forum/contributor-badge.enum";
import { CategoryInterface } from "@core/interfaces/forums/category.interface";
import {
  CommunityNoteInterface,
  CommunityNoteHistoryInterface
} from "@core/interfaces/forums/community-note.interface";
import { ForumInterface } from "@core/interfaces/forums/forum.interface";
import { PostBadgesState, PostBadgeType, PostBadgeDetail } from "@core/interfaces/forums/post-badge.interface";
import { PostInterface } from "@core/interfaces/forums/post.interface";
import { TagInterface } from "@core/interfaces/forums/tag.interface";
import { TopicInterface } from "@core/interfaces/forums/topic.interface";

import { All, ForumActionTypes } from "./forum.actions";

export interface ForumState {
  categories: CategoryInterface[] | null;
  totalCategories: number | null;

  forums: ForumInterface[] | null;
  totalForums: number | null;

  topics: TopicInterface[] | null;
  totalTopics: number | null;
  hasMoreTopics: boolean;

  posts: PostInterface[] | null;
  totalPosts: number | null;
  hasMorePosts: boolean;

  tags: TagInterface[] | null;
  totalTags: number | null;
  tagsLoading: boolean;

  currentCategory: CategoryInterface | null;
  currentForum: ForumInterface | null;
  currentTopic: TopicInterface | null;
  currentPost: PostInterface | null;

  contributorBadges: Record<number, Record<number, ContributorBadge[]>>;
  postBadges: PostBadgesState;
  postLikeCounts: Record<PostInterface["id"], number>;
  userLikedPosts: PostInterface["id"][];
  postContentTypeId: number | null;

  communityNotes: {
    byPostId: Record<number, CommunityNoteInterface[]>;
    loadingByPostId: Record<number, boolean>;
    errorByPostId: Record<number, string>;
    userNotesByPostId: Record<number, number>;
    votedNoteIds: number[];
    eligibilityCache: Record<number, boolean>;
    loadedPostIds: number[];
    originalNotesBeforeUpdate: Record<number, CommunityNoteInterface>; // Store original note for reverting on error
    historyByNoteId: Record<number, CommunityNoteHistoryInterface>;
    historyLoadingByNoteId: Record<number, boolean>;
  };

  error: string | null;
}

export const initialForumState: ForumState = {
  categories: null,
  totalCategories: null,

  forums: null,
  totalForums: null,

  topics: null,
  totalTopics: null,
  hasMoreTopics: false,

  posts: null,
  totalPosts: null,
  hasMorePosts: false,

  tags: null,
  totalTags: null,
  tagsLoading: false,

  currentCategory: null,
  currentForum: null,
  currentTopic: null,
  currentPost: null,

  contributorBadges: {},
  postBadges: {},
  postLikeCounts: {},
  userLikedPosts: [],
  postContentTypeId: null,

  communityNotes: {
    byPostId: {},
    loadingByPostId: {},
    errorByPostId: {},
    userNotesByPostId: {},
    votedNoteIds: [],
    eligibilityCache: {},
    loadedPostIds: [],
    originalNotesBeforeUpdate: {},
    historyByNoteId: {},
    historyLoadingByNoteId: {}
  },

  error: null
};

export function forumReducer(
  state = initialForumState,
  action: All | CreateTogglePropertySuccess | DeleteTogglePropertySuccess
): ForumState {
  switch (action.type) {
    case ForumActionTypes.LOAD_CATEGORIES:
    case ForumActionTypes.LOAD_FORUMS:
    case ForumActionTypes.LOAD_TOPICS:
    case ForumActionTypes.LOAD_POSTS:
    case ForumActionTypes.CREATE_POST:
    case ForumActionTypes.LOAD_TAGS:
    case ForumActionTypes.GET_CATEGORY:
    case ForumActionTypes.GET_FORUM:
    case ForumActionTypes.GET_POST:
    case ForumActionTypes.LOAD_CONTRIBUTOR_BADGES:
    case ForumActionTypes.MARK_POST_AS_READ:
      return {
        ...state,
        error: null,
        tagsLoading: action.type === ForumActionTypes.LOAD_TAGS ? true : state.tagsLoading
      };

    case ForumActionTypes.GET_TOPIC:
      // Clear current topic and forum when loading a new topic to prevent stale breadcrumbs
      return {
        ...state,
        currentTopic: null,
        currentForum: null,
        error: null
      };

    case ForumActionTypes.LOAD_CATEGORIES_SUCCESS:
      return {
        ...state,
        categories: action.payload.categories,
        totalCategories: action.payload.total
      };

    case ForumActionTypes.LOAD_FORUMS_SUCCESS:
      return {
        ...state,
        forums: action.payload.forums,
        totalForums: action.payload.total
      };

    case ForumActionTypes.LOAD_TOPICS_SUCCESS:
      // For loading more topics with infinite scroll:
      // If we dispatched an action with page > 1, we should append to the existing topics
      // Otherwise, replace the topics with the new ones
      const existingTopics = state.topics || [];
      let updatedTopics = action.payload.topics;

      // If the topics array is the same length or shorter than what we already have,
      // this must be the first page or a refresh
      if (
        existingTopics.length > 0 &&
        action.payload.topics.length > 0 &&
        action.payload.topics[0].id !== existingTopics[0].id
      ) {
        // Different topics than what we have - this is a new page, append
        updatedTopics = [...existingTopics, ...action.payload.topics];
      }

      return {
        ...state,
        topics: updatedTopics,
        totalTopics: action.payload.total,
        hasMoreTopics: action.payload.hasNext
      };

    case ForumActionTypes.LOAD_POSTS_SUCCESS:
      return {
        ...state,
        posts: action.payload.posts,
        totalPosts: action.payload.total,
        hasMorePosts: action.payload.hasNext !== undefined ? action.payload.hasNext : false
      };

    case ForumActionTypes.CREATE_POST_SUCCESS:
      return {
        ...state,
        // Append the new post to the existing posts
        posts: state.posts ? [...state.posts, action.payload.post] : [action.payload.post],
        // Increment total posts count
        totalPosts: state.totalPosts ? state.totalPosts + 1 : 1,
        // Also update the current topic's post count if we have it
        currentTopic: state.currentTopic
          ? { ...state.currentTopic, postCount: (state.currentTopic.postCount || 0) + 1 }
          : state.currentTopic
      };

    case ForumActionTypes.LOAD_TAGS_SUCCESS:
      return {
        ...state,
        tags: action.payload.append && state.tags ? [...state.tags, ...action.payload.tags] : action.payload.tags,
        totalTags: action.payload.total,
        tagsLoading: false
      };

    case ForumActionTypes.GET_CATEGORY_SUCCESS:
      return {
        ...state,
        currentCategory: action.payload.category
      };

    case ForumActionTypes.GET_FORUM_SUCCESS:
      return {
        ...state,
        currentForum: action.payload.forum
      };

    case ForumActionTypes.GET_TOPIC_SUCCESS:
      return {
        ...state,
        currentTopic: action.payload.topic
      };

    case ForumActionTypes.GET_POST_SUCCESS:
      return {
        ...state,
        currentPost: action.payload.post
      };

    case ForumActionTypes.CLEAR_TOPICS:
      return {
        ...state,
        topics: null,
        totalTopics: null,
        hasMoreTopics: false
      };

    case ForumActionTypes.CLEAR_POSTS:
      return {
        ...state,
        posts: null,
        totalPosts: null,
        hasMorePosts: false,
        currentTopic: null // Also clear the current topic
      };

    case ForumActionTypes.LOAD_CONTRIBUTOR_BADGES_SUCCESS:
      // Process badges and convert string IDs to numbers
      const topicId = action.payload.topicId;
      const badgesMap: Record<number, ContributorBadge[]> = {};

      // Convert string IDs to numbers
      Object.entries(action.payload.badges).forEach(([userId, badges]) => {
        const userIdNumber = parseInt(userId, 10);
        badgesMap[userIdNumber] = badges;
      });

      // Store badges with topic context
      const updatedBadges = { ...state.contributorBadges };
      Object.entries(badgesMap).forEach(([userId, badges]) => {
        const userIdNumber = parseInt(userId, 10);
        if (!updatedBadges[topicId]) {
          updatedBadges[topicId] = {};
        }
        updatedBadges[topicId][userIdNumber] = badges;
      });

      return {
        ...state,
        contributorBadges: updatedBadges
      };

    case ForumActionTypes.LOAD_TOPIC_LIST_BADGES_SUCCESS:
      // Process badges from multiple topics
      const updatedTopicBadges = { ...state.contributorBadges };

      // Convert the topic ID and user ID to numbers and extract badges
      Object.entries(action.payload.badges).forEach(([topicIdStr, data]) => {
        const topicIdNum = parseInt(topicIdStr, 10);
        const userId = parseInt(data.userId, 10);

        if (!updatedTopicBadges[topicIdNum]) {
          updatedTopicBadges[topicIdNum] = {};
        }
        updatedTopicBadges[topicIdNum][userId] = data.badges;
      });

      return {
        ...state,
        contributorBadges: updatedTopicBadges
      };

    case ForumActionTypes.LOAD_POST_BADGES_SUCCESS:
      // Process post badges and convert string IDs to numbers and strings to PostBadgeType
      const postBadgesMap: { [postId: number]: { [badgeType in PostBadgeType]?: PostBadgeDetail } } = {};

      Object.entries(action.payload.badges).forEach(([postId, badges]) => {
        const postIdNumber = parseInt(postId, 10);
        postBadgesMap[postIdNumber] = badges as { [badgeType in PostBadgeType]?: PostBadgeDetail };
      });

      return {
        ...state,
        postBadges: {
          ...state.postBadges,
          [action.payload.topicId]: postBadgesMap
        }
      };

    // Handle poll voting success by updating the currentTopic with the updated topic
    case ForumActionTypes.VOTE_POLL_SUCCESS:
    case ForumActionTypes.CANCEL_POLL_VOTE_SUCCESS:
    case ForumActionTypes.MARK_POST_AS_READ_SUCCESS:
    case ForumActionTypes.UPDATE_TOPIC_TAGS_SUCCESS:
      return {
        ...state,
        currentTopic: action.payload.topic,
        // If the topic is in the topics list, update it there too
        topics: state.topics
          ? state.topics.map(topic => (topic.id === action.payload.topic.id ? action.payload.topic : topic))
          : state.topics
      };

    case ForumActionTypes.LOAD_CATEGORIES_FAILURE:
    case ForumActionTypes.LOAD_FORUMS_FAILURE:
    case ForumActionTypes.LOAD_TOPICS_FAILURE:
    case ForumActionTypes.LOAD_POSTS_FAILURE:
    case ForumActionTypes.CREATE_POST_FAILURE:
    case ForumActionTypes.LOAD_TAGS_FAILURE:
    case ForumActionTypes.GET_CATEGORY_FAILURE:
    case ForumActionTypes.GET_FORUM_FAILURE:
    case ForumActionTypes.GET_TOPIC_FAILURE:
    case ForumActionTypes.GET_POST_FAILURE:
    case ForumActionTypes.LOAD_CONTRIBUTOR_BADGES_FAILURE:
    case ForumActionTypes.LOAD_TOPIC_LIST_BADGES_FAILURE:
    case ForumActionTypes.LOAD_POST_BADGES_FAILURE:
    case ForumActionTypes.LOAD_POST_LIKES_FAILURE:
    case ForumActionTypes.VOTE_POLL_FAILURE:
    case ForumActionTypes.CANCEL_POLL_VOTE_FAILURE:
    case ForumActionTypes.DELETE_POST_FAILURE:
    case ForumActionTypes.APPROVE_POST_FAILURE:
    case ForumActionTypes.MARK_POST_AS_READ_FAILURE:
      return {
        ...state,
        error: action.payload.error,
        tagsLoading: action.type === ForumActionTypes.LOAD_TAGS_FAILURE ? false : state.tagsLoading
      };

    // Handle delete post success
    case ForumActionTypes.DELETE_POST_SUCCESS:
      return {
        ...state,
        // Remove the deleted post from the posts list
        posts: state.posts ? state.posts.filter(post => post.id !== action.payload.postId) : state.posts,
        // If we just deleted the currentPost, clear it
        currentPost: state.currentPost && state.currentPost.id === action.payload.postId ? null : state.currentPost
      };

    // Handle approve post success
    case ForumActionTypes.APPROVE_POST_SUCCESS:
      return {
        ...state,
        // Update the post in the posts list
        posts: state.posts
          ? state.posts.map(post => (post.id === action.payload.post.id ? { ...post, onModeration: false } : post))
          : state.posts,
        // If we just approved the currentPost, update it
        currentPost:
          state.currentPost && state.currentPost.id === action.payload.post.id
            ? { ...state.currentPost, onModeration: false }
            : state.currentPost
      };

    // Handle subscription actions
    case ForumActionTypes.SUBSCRIBE_TOPIC_SUCCESS:
      return {
        ...state,
        // Update the currentTopic if it matches the topic being subscribed to
        currentTopic:
          state.currentTopic && state.currentTopic.id === action.payload.topicId
            ? { ...state.currentTopic, subscribed: true }
            : state.currentTopic,
        // Also update the topic in the topics list if it exists
        topics: state.topics
          ? state.topics.map(topic => (topic.id === action.payload.topicId ? { ...topic, subscribed: true } : topic))
          : state.topics
      };

    case ForumActionTypes.UNSUBSCRIBE_TOPIC_SUCCESS:
      return {
        ...state,
        // Update the currentTopic if it matches the topic being unsubscribed from
        currentTopic:
          state.currentTopic && state.currentTopic.id === action.payload.topicId
            ? { ...state.currentTopic, subscribed: false }
            : state.currentTopic,
        // Also update the topic in the topics list if it exists
        topics: state.topics
          ? state.topics.map(topic => (topic.id === action.payload.topicId ? { ...topic, subscribed: false } : topic))
          : state.topics
      };

    // Post Likes cases
    case ForumActionTypes.LOAD_POST_LIKES_SUCCESS:
      return {
        ...state,
        postLikeCounts: action.payload.likeCounts,
        userLikedPosts: action.payload.userLikedPosts
      };

    // Set Post Content Type
    case ForumActionTypes.SET_POST_CONTENT_TYPE:
      return {
        ...state,
        postContentTypeId: action.payload.contentTypeId
      };

    // Community Notes cases
    case ForumActionTypes.LOAD_COMMUNITY_NOTES:
      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          loadingByPostId: {
            ...state.communityNotes.loadingByPostId,
            [action.payload.postId]: true
          },
          errorByPostId: {
            ...state.communityNotes.errorByPostId,
            [action.payload.postId]: null
          }
        }
      };

    case ForumActionTypes.LOAD_COMMUNITY_NOTES_SUCCESS:
      const userNotes = {};
      const votedNotes = [];

      action.payload.notes.forEach(note => {
        if (note.canEdit) {
          userNotes[action.payload.postId] = note.id;
        }
        if (note.userHasVoted) {
          votedNotes.push(note.id);
        }
      });

      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          byPostId: {
            ...state.communityNotes.byPostId,
            [action.payload.postId]: action.payload.notes
          },
          loadingByPostId: {
            ...state.communityNotes.loadingByPostId,
            [action.payload.postId]: false
          },
          userNotesByPostId: {
            ...state.communityNotes.userNotesByPostId,
            ...userNotes
          },
          votedNoteIds: [...new Set([...state.communityNotes.votedNoteIds, ...votedNotes])],
          loadedPostIds: [...new Set([...state.communityNotes.loadedPostIds, action.payload.postId])]
        }
      };

    case ForumActionTypes.LOAD_COMMUNITY_NOTES_FAILURE:
      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          loadingByPostId: {
            ...state.communityNotes.loadingByPostId,
            [action.payload.postId]: false
          },
          errorByPostId: {
            ...state.communityNotes.errorByPostId,
            [action.payload.postId]: action.payload.error
          }
        }
      };

    case ForumActionTypes.CREATE_COMMUNITY_NOTE_SUCCESS:
      const postId = action.payload.note.post;
      const existingNotes = state.communityNotes.byPostId[postId] || [];

      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          byPostId: {
            ...state.communityNotes.byPostId,
            [postId]: [...existingNotes, action.payload.note]
          },
          userNotesByPostId: {
            ...state.communityNotes.userNotesByPostId,
            [postId]: action.payload.note.id
          }
        }
      };

    case ForumActionTypes.UPDATE_COMMUNITY_NOTE:
      // Optimistic update - apply changes immediately
      let optimisticPostId: number | null = null;
      let originalNote: CommunityNoteInterface | null = null;

      // Find the note and its post
      Object.entries(state.communityNotes.byPostId).forEach(([postId, notes]) => {
        const foundNote = notes.find(note => note.id === action.payload.noteId);
        if (foundNote) {
          optimisticPostId = parseInt(postId);
          originalNote = foundNote;
        }
      });

      if (!optimisticPostId || !originalNote) {
        return state;
      }

      // Create the optimistically updated note
      const optimisticNote: CommunityNoteInterface = {
        ...originalNote,
        noteType:
          action.payload.updates.note_type !== undefined ? action.payload.updates.note_type : originalNote.noteType,
        body: action.payload.updates.body !== undefined ? action.payload.updates.body : originalNote.body,
        sources: action.payload.updates.sources !== undefined ? action.payload.updates.sources : originalNote.sources,
        updatedAt: new Date().toISOString(),
        editCount: originalNote.editCount + 1
      };

      // Create a new array with new object references for proper change detection
      const optimisticNotes = [...state.communityNotes.byPostId[optimisticPostId]].map(note =>
        note.id === action.payload.noteId ? { ...optimisticNote } : { ...note }
      );

      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          byPostId: {
            ...state.communityNotes.byPostId,
            [optimisticPostId]: optimisticNotes
          },
          originalNotesBeforeUpdate: {
            ...state.communityNotes.originalNotesBeforeUpdate,
            [action.payload.noteId]: originalNote
          }
        }
      };

    case ForumActionTypes.UPDATE_COMMUNITY_NOTE_SUCCESS:
      const updatePostId = action.payload.note.post;
      // Create a new array with the updated note to ensure proper change detection
      const updatedNotes = [...(state.communityNotes.byPostId[updatePostId] || [])].map(note =>
        note.id === action.payload.note.id ? { ...action.payload.note } : { ...note }
      );

      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          byPostId: {
            ...state.communityNotes.byPostId,
            [updatePostId]: updatedNotes
          },
          originalNotesBeforeUpdate: {
            ...state.communityNotes.originalNotesBeforeUpdate,
            [action.payload.note.id]: undefined // Clean up the stored original
          }
        }
      };

    case ForumActionTypes.UPDATE_COMMUNITY_NOTE_FAILURE:
      // Revert the optimistic update
      // First, extract the noteId from the error message or find it from originalNotesBeforeUpdate
      const failedNoteId = Object.keys(state.communityNotes.originalNotesBeforeUpdate)
        .map(id => parseInt(id))
        .find(id => state.communityNotes.originalNotesBeforeUpdate[id]);

      if (!failedNoteId) {
        return state;
      }

      const originalNoteToRevert = state.communityNotes.originalNotesBeforeUpdate[failedNoteId];
      if (!originalNoteToRevert) {
        return state;
      }

      // Find the post containing this note
      let revertPostId: number | null = null;
      Object.entries(state.communityNotes.byPostId).forEach(([postId, notes]) => {
        if (notes.some(note => note.id === failedNoteId)) {
          revertPostId = parseInt(postId);
        }
      });

      if (!revertPostId) {
        return state;
      }

      // Revert to the original note
      const revertedNotes = state.communityNotes.byPostId[revertPostId].map(note =>
        note.id === failedNoteId ? originalNoteToRevert : note
      );

      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          byPostId: {
            ...state.communityNotes.byPostId,
            [revertPostId]: revertedNotes
          },
          originalNotesBeforeUpdate: {
            ...state.communityNotes.originalNotesBeforeUpdate,
            [failedNoteId]: undefined // Clean up the stored original
          },
          errorByPostId: {
            ...state.communityNotes.errorByPostId,
            [revertPostId]: action.payload.error
          }
        }
      };

    case ForumActionTypes.DELETE_COMMUNITY_NOTE_SUCCESS:
      // Find which post contained this note
      let deletePostId: number | null = null;
      Object.entries(state.communityNotes.byPostId).forEach(([postId, notes]) => {
        if (notes.some(note => note.id === action.payload.noteId)) {
          deletePostId = parseInt(postId);
        }
      });

      if (!deletePostId) {
        return state;
      }

      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          byPostId: {
            ...state.communityNotes.byPostId,
            [deletePostId]: state.communityNotes.byPostId[deletePostId].filter(
              note => note.id !== action.payload.noteId
            )
          },
          userNotesByPostId: {
            ...state.communityNotes.userNotesByPostId,
            [deletePostId]:
              state.communityNotes.userNotesByPostId[deletePostId] === action.payload.noteId
                ? null
                : state.communityNotes.userNotesByPostId[deletePostId]
          },
          votedNoteIds: state.communityNotes.votedNoteIds.filter(id => id !== action.payload.noteId)
        }
      };

    case ForumActionTypes.UPVOTE_COMMUNITY_NOTE:
      // Optimistic update - immediately update the UI
      let upvotePostId: number | null = null;
      const optimisticUpvotedNotes = { ...state.communityNotes.byPostId };
      let originalUpvoteNote: CommunityNoteInterface | null = null;

      Object.entries(optimisticUpvotedNotes).forEach(([postId, notes]) => {
        const noteIndex = notes.findIndex(note => note.id === action.payload.noteId);
        if (noteIndex !== -1) {
          upvotePostId = parseInt(postId);
          originalUpvoteNote = notes[noteIndex];
          optimisticUpvotedNotes[postId] = [...notes];
          optimisticUpvotedNotes[postId][noteIndex] = {
            ...notes[noteIndex],
            voteCount: notes[noteIndex].voteCount + 1,
            userHasVoted: true
          };
        }
      });

      if (!upvotePostId || !originalUpvoteNote) {
        return state;
      }

      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          byPostId: optimisticUpvotedNotes,
          votedNoteIds: [...state.communityNotes.votedNoteIds, action.payload.noteId],
          originalNotesBeforeUpdate: {
            ...state.communityNotes.originalNotesBeforeUpdate,
            [`upvote_${action.payload.noteId}`]: originalUpvoteNote
          }
        }
      };

    case ForumActionTypes.UPVOTE_COMMUNITY_NOTE_SUCCESS:
      // Clean up the stored original note after successful update
      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          originalNotesBeforeUpdate: {
            ...state.communityNotes.originalNotesBeforeUpdate,
            [`upvote_${action.payload.noteId}`]: undefined
          }
        }
      };

    case ForumActionTypes.REMOVE_UPVOTE_COMMUNITY_NOTE:
      // Optimistic update - immediately update the UI
      let removeVotePostId: number | null = null;
      const optimisticRemovedVoteNotes = { ...state.communityNotes.byPostId };
      let originalRemoveVoteNote: CommunityNoteInterface | null = null;

      Object.entries(optimisticRemovedVoteNotes).forEach(([postId, notes]) => {
        const noteIndex = notes.findIndex(note => note.id === action.payload.noteId);
        if (noteIndex !== -1) {
          removeVotePostId = parseInt(postId);
          originalRemoveVoteNote = notes[noteIndex];
          optimisticRemovedVoteNotes[postId] = [...notes];
          optimisticRemovedVoteNotes[postId][noteIndex] = {
            ...notes[noteIndex],
            voteCount: Math.max(0, notes[noteIndex].voteCount - 1),
            userHasVoted: false
          };
        }
      });

      if (!removeVotePostId || !originalRemoveVoteNote) {
        return state;
      }

      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          byPostId: optimisticRemovedVoteNotes,
          votedNoteIds: state.communityNotes.votedNoteIds.filter(id => id !== action.payload.noteId),
          originalNotesBeforeUpdate: {
            ...state.communityNotes.originalNotesBeforeUpdate,
            [`removevote_${action.payload.noteId}`]: originalRemoveVoteNote
          }
        }
      };

    case ForumActionTypes.REMOVE_UPVOTE_COMMUNITY_NOTE_SUCCESS:
      // Clean up the stored original note after successful update
      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          originalNotesBeforeUpdate: {
            ...state.communityNotes.originalNotesBeforeUpdate,
            [`removevote_${action.payload.noteId}`]: undefined
          }
        }
      };

    case ForumActionTypes.LOAD_COMMUNITY_NOTE_HISTORY:
      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          historyLoadingByNoteId: {
            ...state.communityNotes.historyLoadingByNoteId,
            [action.payload.noteId]: true
          }
        }
      };

    case ForumActionTypes.LOAD_COMMUNITY_NOTE_HISTORY_SUCCESS:
      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          historyByNoteId: {
            ...state.communityNotes.historyByNoteId,
            [action.payload.history.noteId]: action.payload.history
          },
          historyLoadingByNoteId: {
            ...state.communityNotes.historyLoadingByNoteId,
            [action.payload.history.noteId]: false
          }
        }
      };

    case ForumActionTypes.LOAD_COMMUNITY_NOTE_HISTORY_FAILURE:
      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          historyLoadingByNoteId: {
            ...state.communityNotes.historyLoadingByNoteId,
            [action.payload.noteId]: false
          }
        }
      };

    case ForumActionTypes.UPVOTE_COMMUNITY_NOTE_FAILURE:
      // Revert the optimistic update for upvote
      const upvoteKey = `upvote_${action.payload.noteId}`;
      const originalUpvoteNoteData = state.communityNotes.originalNotesBeforeUpdate[upvoteKey];

      if (!originalUpvoteNoteData) {
        return state;
      }

      // Find which post contains this note
      let revertUpvotePostId: number | null = null;
      Object.entries(state.communityNotes.byPostId).forEach(([postId, notes]) => {
        if (notes.some(note => note.id === action.payload.noteId)) {
          revertUpvotePostId = parseInt(postId);
        }
      });

      if (!revertUpvotePostId) {
        return state;
      }

      // Revert to the original note
      const revertedUpvoteNotes = state.communityNotes.byPostId[revertUpvotePostId].map(note =>
        note.id === action.payload.noteId ? originalUpvoteNoteData : note
      );

      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          byPostId: {
            ...state.communityNotes.byPostId,
            [revertUpvotePostId]: revertedUpvoteNotes
          },
          votedNoteIds: state.communityNotes.votedNoteIds.filter(id => id !== action.payload.noteId),
          originalNotesBeforeUpdate: {
            ...state.communityNotes.originalNotesBeforeUpdate,
            [upvoteKey]: undefined
          }
        }
      };

    case ForumActionTypes.REMOVE_UPVOTE_COMMUNITY_NOTE_FAILURE:
      // Revert the optimistic update for remove upvote
      const removeVoteKey = `removevote_${action.payload.noteId}`;
      const originalRemoveVoteNoteData = state.communityNotes.originalNotesBeforeUpdate[removeVoteKey];

      if (!originalRemoveVoteNoteData) {
        return state;
      }

      // Find which post contains this note
      let revertRemoveVotePostId: number | null = null;
      Object.entries(state.communityNotes.byPostId).forEach(([postId, notes]) => {
        if (notes.some(note => note.id === action.payload.noteId)) {
          revertRemoveVotePostId = parseInt(postId);
        }
      });

      if (!revertRemoveVotePostId) {
        return state;
      }

      // Revert to the original note
      const revertedRemoveVoteNotes = state.communityNotes.byPostId[revertRemoveVotePostId].map(note =>
        note.id === action.payload.noteId ? originalRemoveVoteNoteData : note
      );

      return {
        ...state,
        communityNotes: {
          ...state.communityNotes,
          byPostId: {
            ...state.communityNotes.byPostId,
            [revertRemoveVotePostId]: revertedRemoveVoteNotes
          },
          votedNoteIds: [...state.communityNotes.votedNoteIds, action.payload.noteId], // Re-add to voted list
          originalNotesBeforeUpdate: {
            ...state.communityNotes.originalNotesBeforeUpdate,
            [removeVoteKey]: undefined
          }
        }
      };

    // Handle toggle property actions for post likes
    case AppActionTypes.CREATE_TOGGLE_PROPERTY_SUCCESS: {
      const toggleProperty = (action as CreateTogglePropertySuccess).payload.toggleProperty;

      // Only handle 'like' property type for posts
      if (toggleProperty.propertyType !== "like" || !state.postContentTypeId) {
        return state;
      }

      // Check if this is a post like by verifying the content type
      if (toggleProperty.contentType !== state.postContentTypeId) {
        return state;
      }

      // Update the like count and add to user liked posts
      return {
        ...state,
        postLikeCounts: {
          ...state.postLikeCounts,
          [toggleProperty.objectId]: (state.postLikeCounts[toggleProperty.objectId] || 0) + 1
        },
        userLikedPosts: [...state.userLikedPosts, toggleProperty.objectId]
      };
    }

    case AppActionTypes.DELETE_TOGGLE_PROPERTY_SUCCESS: {
      const toggleProperty = (action as DeleteTogglePropertySuccess).payload.toggleProperty;

      // Only handle 'like' property type for posts
      if (toggleProperty.propertyType !== "like" || !state.postContentTypeId || !toggleProperty.objectId) {
        return state;
      }

      // Check if this is a post like by verifying the content type
      if (toggleProperty.contentType !== state.postContentTypeId) {
        return state;
      }

      // Update the like count and remove from user liked posts
      const currentCount = state.postLikeCounts[toggleProperty.objectId] || 0;

      return {
        ...state,
        postLikeCounts: {
          ...state.postLikeCounts,
          [toggleProperty.objectId]: Math.max(0, currentCount - 1)
        },
        userLikedPosts: state.userLikedPosts.filter(postId => postId !== toggleProperty.objectId)
      };
    }

    default:
      return state;
  }
}
